/* eslint-disable no-unused-vars */
import { InvestmentTransferStatus, InvestmentTransferType } from '@egw/api'
import { useTranslation } from '@egw/i18n'
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import cx from 'classnames'
import { type FC } from 'react'

export interface TransactionSubwayProps {
  /**
   * Transfer Status
   */
  transferStatus: InvestmentTransferStatus

  /**
   * Transfer Type Withdrawal | Deposit
   */
  investmentTransferType: InvestmentTransferType

  /**
   * Checking Product Name
   */
  checkingProductName: string
}

enum SubwayStatusEnum {
  STEP_1 = 'STEP_1',
  STEP_2 = 'STEP_2',
  STEP_3 = 'STEP_3',
}

export const TransactionSubway: FC<TransactionSubwayProps> = ({
  transferStatus,
  investmentTransferType,
  checkingProductName,
}) => {
  const { t } = useTranslation('features-transactions')

  const subwayStatus = ((): SubwayStatusEnum => {
    if (investmentTransferType === InvestmentTransferType.DEPOSIT) {
      switch (transferStatus) {
        case InvestmentTransferStatus.PROCESSING_FUNDS:
          return SubwayStatusEnum.STEP_1
        case InvestmentTransferStatus.PROCESSING_TRADES:
        case InvestmentTransferStatus.FUNDED:
          return SubwayStatusEnum.STEP_2
        case InvestmentTransferStatus.TRADES_COMPLETE:
          return SubwayStatusEnum.STEP_3
      }
    }
    // Else: Withdrawal
    switch (transferStatus) {
      case InvestmentTransferStatus.PROCESSING_TRADES:
        return SubwayStatusEnum.STEP_1
      case InvestmentTransferStatus.PROCESSING_FUNDS:
        return SubwayStatusEnum.STEP_2
      case InvestmentTransferStatus.FUNDED:
      case InvestmentTransferStatus.TRADES_COMPLETE:
        return SubwayStatusEnum.STEP_3
    }
  })()

  if (subwayStatus === SubwayStatusEnum.STEP_3) return null

  return (
    <div
      data-testid="transaction-subway"
      className="flex rounded-lg pt-4 pb-1 px-8 bg-alertSuccessBg w-full flex flex-col gap-1"
    >
      <p className="font-bold text-xs text-alertSuccessTitle">{t('activity.pending')}</p>
      <p className="text-alertSuccessText text-sm">
        {t(`activity.pending_description.${investmentTransferType}.${subwayStatus}`, { checkingProductName })}
      </p>
      <div className="flex flex-row mt-4">
        <div className="flex flex-col w-1/3 text-center">
          <div className="flex flex-row justify-center items-center">
            <hr className="w-[50%] border-alertSuccessTitle" />
            <CheckCircleIcon data-testid="check-circle-icon" className="mx-auto h-4 w-4 fill-alertSuccessTitle" />
            <hr className="w-[50%] border-alertSuccessTitle" />
          </div>
          <p
            className={cx(
              'font-semibold px-4 md:px-8 flex text-center text-alertSuccessTitle mt-2 text-[10px] self-center md:text-sm md:justify-center',
            )}
          >
            {t(`activity.${investmentTransferType}.STEP_1`)}
          </p>
        </div>
        <div className="flex flex-col w-1/3 text-center">
          <div className="flex flex-row justify-center items-center">
            <hr
              className={cx('w-[50%]', {
                'border-btnSecondaryTextDisabled': subwayStatus === SubwayStatusEnum.STEP_1,
                'border-alertSuccessTitle': subwayStatus === SubwayStatusEnum.STEP_2,
              })}
            />
            {subwayStatus === SubwayStatusEnum.STEP_2 ? (
              <CheckCircleIcon data-testid="check-circle-icon" className="mx-auto h-4 w-4 fill-alertSuccessTitle" />
            ) : (
              <EmptyCircle />
            )}
            <hr
              className={cx('w-[50%]', {
                'border-btnSecondaryTextDisabled': subwayStatus === SubwayStatusEnum.STEP_1,
                'border-alertSuccessTitle': subwayStatus === SubwayStatusEnum.STEP_2,
              })}
            />
          </div>
          <p
            className={cx(
              'font-semibold px-4 md:px-8 flex text-center mt-2 text-[10px] md:text-sm self-center md:justify-center',
              {
                'text-btnSecondaryTextDisabled': subwayStatus === SubwayStatusEnum.STEP_1,
                'text-alertSuccessTitle': subwayStatus === SubwayStatusEnum.STEP_2,
              },
            )}
          >
            {t(`activity.${investmentTransferType}.STEP_2`)}
          </p>
        </div>
        <div className="flex flex-col w-1/3 text-center">
          <div className="flex flex-row justify-center items-center">
            <hr className="w-[50%] border-btnSecondaryTextDisabled" />
            <EmptyCircle />
            <hr className="w-[50%] border-btnSecondaryTextDisabled" />
          </div>
          <p
            className={cx(
              'font-semibold px-4 md:px-8 flex text-center text-btnSecondaryTextDisabled mt-2 text-[10px] self-center md:text-sm md:justify-center',
            )}
          >
            {t(`activity.${investmentTransferType}.STEP_3`)}
          </p>
        </div>
      </div>
    </div>
  )
}

export const EmptyCircle: FC = () => (
  <div data-testid="empty-circle-icon" className="h-4 w-4 self-center content-center">
    <div className="h-[12.8px] w-[12.8px] border-btnSecondaryTextDisabled border rounded-xl bg-alertSuccessBg justify-center self-center mx-auto" />
  </div>
)
